/**
 * Serializable DTO for Armenian Belot client sync.
 *
 * **Commands** accepted by the engine (`applyCommand`):
 * - `START_ROUND` — shuffle, deal 3-2-3, open auction.
 * - `BID` — raise contract (level in tens, trump suit or no-trump, optional kapot).
 * - `PASS` — pass in auction.
 * - `CONTRA` / `RECONTRA` — doubling during open auction (`phase === 'auction'`, `auction.phase === 'bidding'`
 *   with a highest bid); not in `declare`.
 * - `END_DECLARE_PHASE` — resolve combination priority and start tricks.
 * - `PLAY_CARD` — `{ seat, payload: { card, declareBella?: boolean } }`.
 *
 * **Engine events** (non-exhaustive; see orchestrator):
 * - `AUCTION_ENDED`, `AUCTION_REDEAL`, `DECLARATIONS_RESOLVED`, `TRICK_STARTED`, `CARD_PLAYED`,
 *   `TRICK_WON`, `ROUND_SCORED`, `MATCH_ENDED`, `CONTRA_APPLIED`, `RECONTRA_APPLIED`.
 *
 * **`roundScoreSheet`** (онлайн-комната): накопительные строки после каждого `ROUND_SCORED`
 * (`roundIndex`, `delta` по командам 0/1, `teamScoresAfter`). Клиент переводит в «Мы / Они» по месту зрителя.
 *
 * **Комбинации:** `declarationMode: 'casual'|'hardcore'` в состоянии движка; хардкор — ручной выбор
 * на первом ходу (см. `engine.js`).
 *
 * @module game/belot/armenian/snapshot
 */

/**
 * @typedef {'init'|'dealing'|'auction'|'declare'|'play'|'score'|'match_end'} BelotArmenianPhase
 */

/**
 * @typedef {'BID'|'PASS'|'CONTRA'|'RECONTRA'|'PLAY_CARD'|'DECLARE_COMBINATIONS'|'DECLARE_BELLA'|'START_ROUND'|'END_DECLARE_PHASE'} BelotArmenianCommandType
 */

/**
 * @typedef {'AUCTION_ENDED'|'AUCTION_REDEAL'|'DECLARATIONS_RESOLVED'|'TRICK_STARTED'|'CARD_PLAYED'|'TRICK_WON'|'ROUND_SCORED'|'MATCH_ENDED'|'CONTRA_APPLIED'|'RECONTRA_APPLIED'} BelotArmenianEventType
 */

/**
 * @typedef {{
 *   schemaVersion: number,
 *   phase: BelotArmenianPhase,
 *   targetScore: number,
 *   teamScores: [number, number],
 *   dealerSeat?: number,
 *   currentTurnSeat?: number|null,
 *   roundIndex?: number,
 *   contract?: Record<string, unknown>|null,
 *   trickIndex?: number,
 *   trickLeadSeat?: number|null,
 *   currentTrick?: Array<{ seat: number, card: number }>,
 *   tricksTakenByTeam?: [number, number],
 *   auction?: { phase: string, highestBid: unknown, bidAuthorSeat: (number|null), contraMultiplier: number }|null,
 *   handsVisible?: 'self_only'|'none'|'debug_all',
 *   hands?: (number[]|null)[],
 *   lastEvents?: Array<{ type: BelotArmenianEventType|string, payload?: Record<string, unknown> }>,
 *   roundScoreSheet?: Array<{ roundIndex: number, delta: [number, number], teamScoresAfter: [number, number] }>,
 * }} BelotArmenianPublicSnapshot
 */

/** Schema version for client migration (increment when DTO fields change). */
const BELOT_ARMENIAN_SNAPSHOT_SCHEMA_VERSION = 5;

/**
 * @param {any} p
 * @returns {{ seat: number, card: number }}
 */
function publicTrickPlay(p) {
  return {
    seat: Number(p.seat),
    card: Number(p.card),
  };
}

/**
 * @param {object} engineState — internal state from {@link ../engine}
 * @param {object} [options]
 * @param {number|null} [options.viewerSeat] — if set with `handsVisible: 'self_only'`, only that hand is shown.
 * @param {'self_only'|'none'|'debug_all'} [options.handsVisible]
 * @param {Array<{ type: string, payload?: Record<string, unknown> }>} [options.lastEvents]
 * @param {Array<{ roundIndex: number, delta: [number, number], teamScoresAfter: [number, number] }>} [options.roundScoreSheet]
 * @returns {BelotArmenianPublicSnapshot}
 */
function buildPublicSnapshot(engineState, options) {
  const opt = options && typeof options === "object" ? options : {};
  const handsVisible = opt.handsVisible || "none";
  const viewerSeat = opt.viewerSeat;
  /** @type {(number[]|null)[]} */
  const handsOut = [null, null, null, null];
  if (engineState.hands && Array.isArray(engineState.hands)) {
    if (handsVisible === "debug_all") {
      for (let i = 0; i < 4; i += 1) {
        handsOut[i] = engineState.hands[i] ? engineState.hands[i].slice() : null;
      }
    } else if (handsVisible === "self_only" && viewerSeat != null) {
      handsOut[viewerSeat] = engineState.hands[viewerSeat]
        ? engineState.hands[viewerSeat].slice()
        : null;
    }
  }
  const auc = engineState.auction;
  const auctionPublic =
    auc && typeof auc === "object"
      ? {
          phase: auc.phase,
          highestBid: auc.highestBid ?? null,
          bidAuthorSeat: auc.bidAuthorSeat ?? null,
          contraMultiplier: auc.contraMultiplier ?? 1,
        }
      : null;
  const dm = engineState.declarationMode === "hardcore" ? "hardcore" : "casual";
  const declaredRows = Array.isArray(engineState.declaredComboKeysBySeat)
    ? engineState.declaredComboKeysBySeat
    : [[], [], [], []];
  /** @type {[string[], string[], string[], string[]]} */
  const declaredComboKeysBySeat = /** @type {any} */ ([0, 1, 2, 3].map((i) =>
    Array.isArray(declaredRows[i]) ? declaredRows[i].map((k) => String(k)) : [],
  ));
  const sb0 = Number(engineState.shulerBonusByTeam?.[0]) || 0;
  const sb1 = Number(engineState.shulerBonusByTeam?.[1]) || 0;
  const shulerEnabled = Boolean(engineState.shulerEnabled);
  const lastOut =
    engineState.lastAccusationOutcome &&
    typeof engineState.lastAccusationOutcome === "object" &&
    engineState.lastAccusationOutcome.type &&
    engineState.lastAccusationOutcome.type !== "none"
      ? {
          type: String(engineState.lastAccusationOutcome.type),
          accuserSeat:
            engineState.lastAccusationOutcome.accuserSeat != null
              ? Number(engineState.lastAccusationOutcome.accuserSeat)
              : undefined,
          cheaterSeat:
            engineState.lastAccusationOutcome.cheaterSeat != null
              ? Number(engineState.lastAccusationOutcome.cheaterSeat)
              : undefined,
          card:
            engineState.lastAccusationOutcome.card != null
              ? Number(engineState.lastAccusationOutcome.card)
              : undefined,
        }
      : null;
  return {
    schemaVersion: BELOT_ARMENIAN_SNAPSHOT_SCHEMA_VERSION,
    phase: engineState.phase,
    targetScore: engineState.targetScore,
    /** Матчевые очки + накопленный за текущий раунд шулер-бонус (лайв). */
    teamScores: /** @type {[number, number]} */ ([
      Number(engineState.teamScores[0]) + sb0,
      Number(engineState.teamScores[1]) + sb1,
    ]),
    shulerEnabled,
    shulerBonusByTeam: /** @type {[number, number]} */ ([sb0, sb1]),
    cheaterRevealedSeat:
      engineState.cheaterRevealedSeat != null ? Number(engineState.cheaterRevealedSeat) : null,
    lastAccusationOutcome: lastOut,
    dealerSeat: engineState.dealerSeat,
    currentTurnSeat: engineState.currentTurnSeat,
    roundIndex: engineState.roundIndex,
    contract: engineState.contract || null,
    trickIndex: engineState.trickIndex,
    trickLeadSeat: engineState.trickLeadSeat,
    currentTrick: Array.isArray(engineState.currentTrick)
      ? engineState.currentTrick.map((p) => publicTrickPlay(p))
      : [],
    lastCompletedTrick:
      engineState.lastCompletedTrick &&
      typeof engineState.lastCompletedTrick === "object" &&
      Array.isArray(engineState.lastCompletedTrick.plays)
        ? {
            trickIndex: Number(engineState.lastCompletedTrick.trickIndex) || 0,
            winnerSeat: Number(engineState.lastCompletedTrick.winnerSeat),
            plays: engineState.lastCompletedTrick.plays.map((p) => ({
              seat: Number(p.seat),
              card: Number(p.card),
            })),
          }
        : null,
    tricksTakenByTeam: engineState.tricksWonByTeam
      ? [...engineState.tricksWonByTeam]
      : [0, 0],
    auction: auctionPublic,
    declarationMode: dm,
    declaredComboKeysBySeat,
    firstPlayDeclarationsCommitted: Array.isArray(engineState.firstPlayDeclarationsCommitted)
      ? /** @type {[boolean, boolean, boolean, boolean]} */ (
          engineState.firstPlayDeclarationsCommitted.map((c) => Boolean(c))
        )
      : [false, false, false, false],
    handsVisible,
    hands: handsOut,
    lastEvents: Array.isArray(opt.lastEvents) ? opt.lastEvents : [],
    roundScoreSheet: Array.isArray(opt.roundScoreSheet)
      ? opt.roundScoreSheet.map((r) => ({
          roundIndex: Number(r.roundIndex) || 0,
          delta: /** @type {[number, number]} */ ([
            Number(r.delta?.[0]) || 0,
            Number(r.delta?.[1]) || 0,
          ]),
          teamScoresAfter: /** @type {[number, number]} */ ([
            Number(r.teamScoresAfter?.[0]) || 0,
            Number(r.teamScoresAfter?.[1]) || 0,
          ]),
        }))
      : [],
  };
}

module.exports = {
  BELOT_ARMENIAN_SNAPSHOT_SCHEMA_VERSION,
  buildPublicSnapshot,
};
